import { CircularProgress } from '@civilgrid/ui/components';
import {
  type Cell,
  type ColumnDef as ColumnDefBase,
  flexRender,
  type Row,
  type RowData,
  type Table,
} from '@tanstack/react-table';
import classNames from 'classnames';
import { debounce, isEmpty } from 'lodash';
import type React from 'react';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useMeasure } from 'react-use';
import Icon from '../Icon';
import GlobalFilter from './GlobalFilter';
import { dateTimeSortingFn } from './helpers';
import Pagination, { type PaginationProps } from './Pagination';
import SearchAsync, { type SearchAsyncColumnDef, type SearchAsyncProps, type SearchRef } from './SearchAsync';
import type { ExportableColumnDef } from './types';

export const AUTO_COL_WIDTH = 999;

export type ColumnDef<TData extends RowData, TValue = any> = ColumnDefBase<TData, TValue> & {
  cellClassName?: string | ((cell: Cell<TData, TValue>) => string);
  columns?: ColumnDef<TData, TValue>[];
  sortIconPosition?: 'right' | 'left';
};

export {
  GlobalFilter,
  Pagination,
  type PaginationProps,
  SearchAsync,
  type SearchAsyncProps,
  type SearchAsyncColumnDef,
  type SearchRef,
  type ExportableColumnDef,
  dateTimeSortingFn,
};

export interface TableProps<TData, TValue> {
  id: string; // unique ID for each table. This is used for persisting state.
  table: Table<TData>;
  onRowClick?: (row: Row<TData>) => void;
  onRowDoubleClick?: (row: Row<TData>) => void;
  onCellClick?: (cell: Cell<TData, TValue>) => void;
  getRowClassName?: (row: Row<TData>) => string;
  evenOdd?: boolean;
  isLoading?: boolean;
  topSection?: ReactNode;
  // Prefix for the header/body tables, each header cell (`-header-<columnId>`) and row (`-row-<rowId>`).
  'data-testid'?: string;
}

// Cloned from index.tsx to make changes to the table component in the style of the new UI for Table V2
// This will be removed once all tables are migrated to the new UI
export function TableV2<TData = any[], TValue = any>({
  id,
  table,
  onRowClick,
  onRowDoubleClick,
  onCellClick,
  getRowClassName,
  evenOdd = true,
  isLoading: _isDataLoading,
  topSection,
  'data-testid': dataTestId,
}: TableProps<TData, TValue>) {
  const [isAutoResizing, setIsAutoResizing] = useState(true);
  const [tableParentRef, parentDims] = useMeasure<HTMLDivElement>();
  const tableHeaderRef = useRef<HTMLDivElement>(null);
  const [topSectionRef, topSectionDims] = useMeasure<HTMLDivElement>();
  const { columnSizing } = table.getState();

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (parentDims.width === 0) {
      return; // not done loading yet
    }
    // load saved widths first
    try {
      const savedWidths = localStorage.getItem(`table-column-size-state-${id}`);
      if (savedWidths) {
        const parsed = JSON.parse(savedWidths);
        table.setColumnSizing(parsed);
      }
    } catch (e) {
      console.error('TableV2: failed to load column sizing', e);
      // Ignore corrupted or inaccessible column size state
    }

    const allColumns = table.getAllColumns();
    let totalAvailable = parentDims.width;
    const colsWithoutSize: string[] = [];
    allColumns.forEach((c) => {
      if (c.getSize() === AUTO_COL_WIDTH) {
        colsWithoutSize.push(c.id);
      } else {
        totalAvailable = totalAvailable - c.getSize();
      }
    });

    if (colsWithoutSize.length > 0) {
      if (totalAvailable > 0) {
        const sizePerCol = totalAvailable / colsWithoutSize.length;
        const newSizing: any = {};
        colsWithoutSize.forEach((c) => {
          newSizing[c] = sizePerCol;
        });

        table.setColumnSizing(newSizing);
      }
    }
    setIsAutoResizing(false);
  }, [parentDims.width]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const debouncedSave = useMemo(
    () =>
      debounce((columnSizing) => {
        try {
          localStorage.setItem(`table-column-size-state-${id}`, JSON.stringify(columnSizing));
        } catch (e) {
          // Storage may be full or disabled; log and swallow
          console.warn('TableV2: failed to persist column sizing', e);
        }
      }, 100),
    [],
  );

  useEffect(() => {
    if (!isEmpty(columnSizing)) {
      debouncedSave(columnSizing);
    }
  }, [columnSizing, debouncedSave]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    try {
      const sortState = sessionStorage.getItem(`table-sort-state-${id}`);
      if (sortState) {
        const parsed = JSON.parse(sortState);
        table.setSorting(parsed);
      }
    } catch (e) {
      console.error('TableV2: failed to load sort state', e);
      // Ignore corrupted or inaccessible sort state
    }
  }, []);

  const testId = (suffix: string) => (dataTestId ? `${dataTestId}-${suffix}` : undefined);

  let contents = <CircularProgress indeterminate />;

  const isLoading = isAutoResizing || _isDataLoading;

  if (!isLoading) {
    // Leaf headers define column widths so resizing one header only changes that column (not the whole group)
    const headerGroups = table.getHeaderGroups();
    const leafHeaders = headerGroups[headerGroups.length - 1]?.headers ?? [];

    contents = (
      <>
        <div ref={tableHeaderRef} className="sticky z-10 overflow-x-hidden" style={{ top: topSectionDims.height + 1 }}>
          <table
            className="w-full border-collapse table-fixed"
            style={{ minWidth: '100%' }}
            data-testid={testId('header')}
          >
            <colgroup>
              {leafHeaders.map((header) => (
                <col key={header.column.id} style={{ width: Math.max(50, header.getSize()) }} />
              ))}
            </colgroup>
            <thead className="border-b border-grey-30 bg-white">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header, j) => {
                    const isResizable =
                      header.getContext().table.options.enableColumnResizing && header.column.getCanResize();

                    let headerSortIcon: React.ReactNode = null;

                    if (header.column.getCanSort()) {
                      headerSortIcon = <div className="h-4 w-4" />;

                      if (header.column.getIsSorted() === 'asc') {
                        headerSortIcon = <Icon icon="arrowUp" />;
                      } else if (header.column.getIsSorted() === 'desc') {
                        headerSortIcon = <Icon icon="arrowDown" />;
                      }
                    }

                    let headerSortIconPosition = 'right';
                    if ((header.column.columnDef as ColumnDef<TData>)?.sortIconPosition === 'left') {
                      headerSortIconPosition = 'left';
                    }

                    return (
                      <th
                        key={header.id}
                        colSpan={header.colSpan}
                        data-testid={header.isPlaceholder ? undefined : testId(`header-${header.column.id}`)}
                        className={classNames(
                          'relative h-8 overflow-hidden border-grey-30 px-2 py-1 text-left',
                          'border-r border-b',
                          j === 0 && 'border-l',
                          header.isPlaceholder && '!border-b-0',
                        )}
                      >
                        <div
                          className={classNames('flex h-full items-center', {
                            'cursor-pointer': header.column.getCanSort() && !header.isPlaceholder,
                          })}
                          onClick={() => {
                            if (table.options.enableSorting && header.column.getCanSort() && !header.isPlaceholder) {
                              const sort = header.column.getIsSorted() === 'asc';
                              header.column.toggleSorting(sort);

                              try {
                                sessionStorage.setItem(
                                  `table-sort-state-${id}`,
                                  JSON.stringify([{ id: header.column.id, desc: sort }]),
                                );
                              } catch (e) {
                                console.warn('TableV2: failed to persist sort state', e);
                              }
                            }
                          }}
                        >
                          {headerSortIconPosition === 'left' &&
                            table.options.enableSorting &&
                            !header.isPlaceholder &&
                            headerSortIcon}
                          <div
                            className={classNames(
                              'flex h-full w-full place-content-start items-center text-xs font-medium text-grey-90 font-noto',
                            )}
                          >
                            {header.isPlaceholder ? (
                              <span>&nbsp;</span>
                            ) : (
                              flexRender(header.column.columnDef.header, header.getContext())
                            )}
                          </div>
                          {headerSortIconPosition === 'right' &&
                            table.options.enableSorting &&
                            !header.isPlaceholder &&
                            headerSortIcon}
                        </div>

                        {isResizable && header.getContext().table.options.columnResizeMode && (
                          <div
                            onMouseDown={(e) => {
                              header.getResizeHandler()(e);
                            }}
                            className="absolute right-0 top-0 h-full w-1 cursor-col-resize select-none hover:bg-blue-50"
                          />
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
          </table>
        </div>
        <div
          onScroll={(e) => {
            tableHeaderRef.current?.scrollTo({
              left: (e.target as HTMLDivElement).scrollLeft,
            });
          }}
          className="overflow-x-auto"
        >
          <table
            className="w-full border-collapse table-fixed"
            style={{ minWidth: '100%' }}
            data-testid={testId('body')}
          >
            <tbody>
              {table.getRowModel().rows.map((row, rowIndex) => {
                const rowClassName = getRowClassName?.(row) || '';
                return (
                  <tr
                    key={row.id}
                    id={`row-${row.id}`}
                    data-testid={testId(`row-${row.id}`)}
                    onClick={() => onRowClick?.(row)}
                    onDoubleClick={() => onRowDoubleClick?.(row)}
                    className={classNames(
                      'h-8 hover:bg-grey-20',
                      rowClassName,
                      { 'cursor-pointer': onRowClick },
                      evenOdd ? (rowIndex % 2 === 0 ? 'bg-white' : 'bg-grey-10') : 'bg-white',
                    )}
                  >
                    {row.getVisibleCells().map((cell, cellIndex) => {
                      const cn = (cell.column.columnDef as ColumnDef<TData>).cellClassName;
                      let cellClassNames = '';

                      if (cn) {
                        cellClassNames = cn instanceof Function ? cn(cell) : cn;
                      }

                      return (
                        <td
                          key={cell.id}
                          className={classNames(
                            'overflow-hidden border-r border-grey-30 px-2 py-1 text-xs font-noto text-grey-90',
                            cellIndex === 0 && 'border-l border-grey-30',
                            cellClassNames,
                          )}
                          style={{ width: Math.max(50, cell.column.getSize()) }}
                          onClick={(e) => {
                            if (onCellClick) {
                              e.stopPropagation();
                              onCellClick(cell as Cell<TData, TValue>);
                            }
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <div className="border-b border-grey-30" data-testid={dataTestId}>
      <div className="sticky top-0 z-10 w-full bg-white border-b border-grey-30" ref={topSectionRef}>
        {topSection}
      </div>
      <div ref={tableParentRef} className="block w-full flex-1 min-h-0">
        {contents}
      </div>
    </div>
  );
}
